import moment from "moment";
import {
  RESOURCE_BREAK,
  RESOURCE_COMPASSIONATE_LEAVE,
  RESOURCE_HOLIDAY,
  RESOURCE_SICK,
  RESOURCE_WORK_HOURS
} from "@/helpers/eventTypes";
import { eventOccupiedDates, eventStartDate } from "@/helpers/rosterSpan";

export const EMPLOYEE_ROSTER_CLEANUP_TYPES = [
  RESOURCE_WORK_HOURS,
  RESOURCE_BREAK,
  RESOURCE_HOLIDAY,
  RESOURCE_SICK,
  RESOURCE_COMPASSIONATE_LEAVE
];

export function isEmployeeRosterCleanupEvent(event) {
  return !!(event && EMPLOYEE_ROSTER_CLEANUP_TYPES.indexOf(event.type) !== -1);
}

function eventDates(event) {
  const occupied = eventOccupiedDates(event);
  if (occupied.length) return occupied;
  const start = eventStartDate(event);
  return start ? [start] : [];
}

function countLabel(count, one, many) {
  if (count === 1) return one;
  return many.replace("{count}", String(count));
}

export function summarizeEmployeeRosterCleanup(events) {
  const rosterEvents = (events || []).filter(isEmployeeRosterCleanupEvent);
  const days = {};

  rosterEvents.forEach(event => {
    eventDates(event).forEach(date => {
      if (!date) return;
      if (!days[date]) {
        days[date] = { work: false, sick: false, away: false };
      }
      if (event.type === RESOURCE_SICK) {
        days[date].sick = true;
        return;
      }
      if (
        event.type === RESOURCE_HOLIDAY ||
        event.type === RESOURCE_COMPASSIONATE_LEAVE
      ) {
        days[date].away = true;
        return;
      }
      days[date].work = true;
    });
  });

  let workDays = 0;
  let sickDays = 0;
  let awayDays = 0;
  Object.keys(days).forEach(date => {
    if (days[date].sick) {
      sickDays += 1;
      return;
    }
    if (days[date].away) {
      awayDays += 1;
      return;
    }
    if (days[date].work) workDays += 1;
  });

  return {
    eventIds: rosterEvents.map(event => event._id).filter(Boolean),
    eventCount: rosterEvents.length,
    dayCount: workDays + sickDays + awayDays,
    workDays,
    sickDays,
    awayDays
  };
}

export function employeeRosterCleanupLines(summary) {
  const data = summary || {};
  const lines = [];
  if (data.workDays) {
    lines.push(countLabel(data.workDays, "1 day on", "{count} days on"));
  }
  if (data.sickDays) {
    lines.push(countLabel(data.sickDays, "1 sick day", "{count} sick days"));
  }
  if (data.awayDays) {
    lines.push(countLabel(data.awayDays, "1 away day", "{count} away days"));
  }
  return lines;
}

function parseTimeMs(value) {
  if (value == null || value === "") return NaN;
  if (typeof value === "number") return value;
  if (value instanceof Date) return value.getTime();
  if (typeof value === "object") {
    if (typeof value.toDate === "function") return value.toDate().getTime();
    if (typeof value.seconds === "number") return value.seconds * 1000;
  }
  const ms = Date.parse(value);
  return Number.isNaN(ms) ? NaN : ms;
}

export function isBlockingEmployeeBooking(booking, nowMs) {
  if (!booking || booking.bookingStatus !== "CONFIRMED") return false;
  const now = typeof nowMs === "number" ? nowMs : Date.now();
  const end = parseTimeMs(booking.end);
  if (!Number.isNaN(end)) return end > now;
  const start = parseTimeMs(booking.start);
  return !Number.isNaN(start) && start > now;
}

export function summarizeEmployeeBookingBlock(bookings, nowMs) {
  const now = typeof nowMs === "number" ? nowMs : Date.now();
  const open = (bookings || []).filter(booking =>
    isBlockingEmployeeBooking(booking, now)
  );
  let lastStart = "";
  let lastMs = 0;
  open.forEach(booking => {
    const ms = parseTimeMs(booking.start);
    if (Number.isNaN(ms) || ms < lastMs) return;
    lastMs = ms;
    lastStart =
      typeof booking.start === "string"
        ? booking.start
        : new Date(ms).toISOString();
  });
  return {
    blocked: open.length > 0,
    count: open.length,
    lastStart
  };
}

export function formatEmployeeBookingWhen(start) {
  if (!start) return "";
  const date = moment.utc(start);
  return date.isValid() ? date.format("ddd D MMM") : "";
}
